import React, { useState, useMemo } from 'react';
import { Box, BoxProps, InputAdornment, Autocomplete, List } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Search as SearchIcon } from 'lucide-react';
import { AxiosError, IAPIClient } from '@askporter/api-client';
import { SearchAutocompleteResult, ErrorModel, SearchAutocompleteRequest } from '@askporter/client-grieg-lyric';
import { useTranslation } from '@askporter/config-provider';
import { useDebounce } from '@askporter/utils';
import { createRenderOption } from '../RevampedAutocomplete/BaseAutocomplete/utils/createRenderOption';
import type { BaseAutocompleteValue } from '../RevampedAutocomplete/types';
import { TextField } from '../TextField';

type AutocompleteUnion =
  | {
      autocompleteEnabled: true;
      /**
       * fires when user changes input value
       */
      onChange?: never;
      /**
       * The API client
       */
      API: () => IAPIClient;
      /**
       * resourceName for the suggestions API
       */
      resourceName:
        | 'tasks'
        | 'assets'
        | 'users'
        | 'teams'
        | 'orgs'
        | 'people'
        | 'tags'
        | 'products'
        | 'product-catalogues';
    }
  | {
      autocompleteEnabled: false;
      onChange: (val: string) => void;
      API?: never;
      resourceName?: never;
    };

export type SearchBoxProps = {
  /**
   * input label text
   */
  label: string;
  /**
   * fires when user hits enter or clicks the search icon
   */
  onSubmit: (searchBoxValue: string) => void;
  /**
   * starting value of the search box
   */
  startValue?: string;
  /**
   * search button aria label text
   */
  searchButtonAria?: string;
  /**
   * handles the differences between figma designs and older uses of this component
   */
  disableSelfSpacing?: boolean;
  /**
   * passes props to top the wrapper box
   */
  wrapperBoxProps?: BoxProps;
} & AutocompleteUnion;

export const SearchBox: React.FC<React.PropsWithChildren<SearchBoxProps>> = ({
  label,
  onSubmit,
  startValue,
  disableSelfSpacing,
  wrapperBoxProps,
  autocompleteEnabled,
  API,
  resourceName,
  onChange,
}: SearchBoxProps) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 700);
  const [hasFocus, setHasFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const shrinkLabel = hasFocus || searchText !== '' ? true : false;

  const payload: SearchAutocompleteRequest = { freeText: debouncedSearchText };
  const { data, isFetched } = useQuery<{ data: SearchAutocompleteResult }, AxiosError<ErrorModel>>({
    queryKey: [`autocomplete/${resourceName}`, payload],

    queryFn: () => {
      if (!API) throw new Error('API is required when autocomplete is enabled');
      return API().post({ path: `autocomplete/${resourceName}`, payload });
    },

    enabled: autocompleteEnabled && debouncedSearchText?.length > 2,
  });

  const hasSearched = debouncedSearchText?.length > 2;
  const shouldShowDropdown = autocompleteEnabled && hasSearched && isFetched && hasFocus;

  // Transform string suggestions into BaseAutocompleteValue objects
  const transformedOptions = useMemo<BaseAutocompleteValue[]>(() => {
    const suggestions = data?.data?.suggestions || [];
    return suggestions.map((suggestion) => ({
      uid: suggestion,
      label: suggestion,
    }));
  }, [data?.data?.suggestions]);

  return (
    <Box {...(disableSelfSpacing ? {} : { px: 6, pb: 6 })} {...wrapperBoxProps}>
      <Autocomplete
        options={transformedOptions}
        freeSolo={!autocompleteEnabled}
        fullWidth
        filterOptions={(x) => x}
        open={shouldShowDropdown && open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={startValue}
        inputValue={searchText}
        onChange={(_, value) => {
          // Handle both string values (freeSolo) and BaseAutocompleteValue objects
          if (typeof value === 'string') {
            onSubmit(value);
          } else {
            onSubmit((value as BaseAutocompleteValue)?.label || '');
          }
        }}
        onInputChange={(_, value) => {
          setSearchText(value || '');
          onChange && onChange(value || '');
        }}
        noOptionsText={<Box sx={{ marginY: 3, marginX: 4 }}>{t('common:search:no_results')}</Box>}
        forcePopupIcon={false}
        getOptionKey={(option: BaseAutocompleteValue | string) => {
          // Handle both string and BaseAutocompleteValue
          if (typeof option === 'string') return option;
          return option.uid;
        }}
        getOptionLabel={(option: BaseAutocompleteValue | string) => {
          // Handle both string and BaseAutocompleteValue
          if (typeof option === 'string') return option;
          return option.label || '';
        }}
        isOptionEqualToValue={(opt: BaseAutocompleteValue, val: BaseAutocompleteValue | string) => {
          if (typeof val === 'string') return opt.label === val;
          return opt.uid === val.uid;
        }}
        renderOption={createRenderOption({
          mapOptionToValue: (option) => option as BaseAutocompleteValue & { label: string },
          isAvatarVisible: false,
          highlightSearchText: true,
          searchQuery: debouncedSearchText,
        })}
        slots={{
          listbox: List,
        }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '2px',
              borderRadius: '8px',
              outline: '1px solid #F2F4F7',
              '& .MuiAutocomplete-noOptions': {
                padding: 0,
              },
              '& .MuiAutocomplete-listbox ': {
                padding: '4px 0',
                maxHeight: '300px',
              },
            },
          },
        }}
        sx={{
          '& .MuiFilledInput-root': {
            paddingTop: '6px',
            paddingBottom: '6px',
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={label}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: shrinkLabel,
              sx: {
                padding: 2,
              },
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                onFocus: () => setHasFocus(true),
                onBlur: () => setHasFocus(false),
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={20} style={{ marginLeft: '2px' }} />
                  </InputAdornment>
                ),
              },
            }}
            inputProps={{
              ...params.inputProps,
              'data-testid': 'autocomplete-search-box-input',
              onKeyDown: (e) => {
                if (e.key === 'Enter') {
                  onSubmit(searchText);
                }
              },
            }}
            sx={{
              backgroundColor: '#fff',
              mt: 0,
              '& .MuiInputBase-input': {
                paddingLeft: '0 !important',
              },
            }}
          />
        )}
      />
    </Box>
  );
};
